import React, { useState } from 'react'
import { Drawer, DrawerProps, Flex, Input, List, Pagination, Typography } from 'antd'
import { useCandidates } from '../hooks'
import { CandidateAvatar } from './candidate-avatar'
import { useTranslation } from 'react-i18next'
import { CandidateModal } from './candidate-modal'
import { TimeAgo } from '../../components/time-ago'
import { CandidateUnarchiveButton } from './candidate-unarchive-button'
import { CandidateCompatibility } from './candidate-compatibility'
import { CommentOutlined } from '@ant-design/icons'

export function CandidateArchiveDrawer({ campaignId, ...rest }: { campaignId?: string } & DrawerProps) {
    const { t } = useTranslation()
    const [search, setSearch] = useState('')
    const [candidateId, setCandidateId] = useState<string | undefined>()
    const [pagination, setPagination] = useState({ page: 1, size: 20 })

    const candidates = useCandidates({
        query: {
            archived: true,
            filters: {
                campaignId: campaignId ? [campaignId] : undefined,
                search: [search],
            },
            sort: '-archived_at',
            ...pagination,
        },
        enabled: rest.open || false,
    })

    return (
        <Drawer
            width={600}
            title={t('Archive')}
            extra={
                <Input.Search
                    onChange={({ target }) => {
                        setPagination({
                            page: 1,
                            size: pagination.size,
                        })
                        setSearch(target.value)
                    }}
                />
            }
            {...rest}
        >
            <Flex
                gap="small"
                justify="space-between"
                className="h-full"
                vertical
            >
                <List
                    size="small"
                    className="-mx-4 max-h-[80vh] overflow-y-scroll"
                    loading={candidates.isFetching}
                    dataSource={candidates.data?.data || []}
                    renderItem={(candidate) => {
                        return (
                            <List.Item
                                tabIndex={0}
                                className="cursor-pointer"
                                onClick={() => {
                                    setCandidateId(candidate.id)
                                }}
                                extra={<CandidateUnarchiveButton candidateId={candidate.id} />}
                            >
                                <List.Item.Meta
                                    avatar={<CandidateAvatar candidate={candidate} />}
                                    description={
                                        <Flex vertical>
                                            {typeof candidate.commentCount !== 'undefined' &&
                                                candidate.commentCount > 0 && (
                                                    <Typography.Text type="secondary">
                                                        <CommentOutlined /> {candidate.commentCount}
                                                    </Typography.Text>
                                                )}
                                            <span>
                                                {candidate.archivedAt && (
                                                    <>
                                                        {t('Archived')}&nbsp;
                                                        <TimeAgo date={candidate.archivedAt} />
                                                    </>
                                                )}
                                            </span>
                                        </Flex>
                                    }
                                    title={
                                        <Flex
                                            className="w-full pr-4"
                                            justify="space-between"
                                            align="center"
                                        >
                                            <Typography.Text
                                                className="mb-0 max-w-[280px]"
                                                ellipsis
                                                title={candidate.name}
                                            >
                                                {candidate.name}
                                            </Typography.Text>
                                            <CandidateCompatibility candidate={candidate} />
                                        </Flex>
                                    }
                                />
                            </List.Item>
                        )
                    }}
                />
                <Pagination
                    align="end"
                    current={candidates.data?.meta.currentPage}
                    defaultPageSize={candidates.data?.meta.perPage}
                    pageSize={candidates.data?.meta.perPage}
                    total={candidates.data?.meta.total}
                    showSizeChanger={true}
                    onChange={(page, size) => {
                        setPagination({
                            page,
                            size,
                        })
                    }}
                />
            </Flex>
            <CandidateModal
                candidateId={candidateId}
                open={!!candidateId}
                onCancel={() => setCandidateId(undefined)}
            />
        </Drawer>
    )
}
